/**
 * Brand-aware confetti with localStorage single-fire guard. ONE TRUE source
 * (was duplicated in 3 apps); extracted in the unification sprint.
 *
 * Brand-aware: reads particle colours from CSS variables on `:root`. Each
 * app provides its own `--color-primary` / `--color-accent` /
 * `--color-on-surface` — that's how customer-pwa's white-label palette
 * survives extraction. Dashboard + agent-pwa get Kilat tokens.
 *
 * Reduced-motion safe (silent no-op). `canvas-confetti` is dynamic-
 * imported on first call so it stays off the initial bundle of any app
 * that hasn't fired a celebration yet.
 *
 * Per [[feedback-dopamine-for-all]].
 */

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function readCssVar(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v.length > 0 ? v : null;
}

function brandedColours(): string[] {
  // Neutral slate, not Kilat green. This fires only when the tenant's palette
  // is unreadable — precisely the moment the white-label app must not paint
  // another company's brand across a celebration.
  const primary = readCssVar('--color-primary') ?? '#475569';
  const accent = readCssVar('--color-accent') ?? '#f59e0b';
  const surfaceFg = readCssVar('--color-on-surface') ?? '#111827';
  return [primary, accent, surfaceFg, '#ffffff'];
}

/**
 * Fire a celebration if we haven't fired this exact `key` before in this
 * browser. Returns `true` if it fired, `false` if it was a repeat (or if
 * reduced-motion is on, or canvas-confetti failed to load).
 */
export async function celebrateOnce(opts: {
  readonly key: string;
  /** Intensity: 'milestone' (loud — first wash, tier-up) or 'micro' (soft). */
  readonly intensity?: 'milestone' | 'micro';
}): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (prefersReducedMotion()) return false;
  const lsKey = `kilat.celebrated.${opts.key}`;
  try {
    if (window.localStorage.getItem(lsKey)) return false;
    window.localStorage.setItem(lsKey, String(Date.now()));
  } catch {
    /* incognito etc. — still fire this session */
  }
  try {
    const { default: confetti } = await import('canvas-confetti');
    const colors = brandedColours();
    const intensity = opts.intensity ?? 'milestone';
    if (intensity === 'milestone') {
      confetti({
        particleCount: 80,
        spread: 60,
        startVelocity: 45,
        origin: { x: 0.15, y: 0.85 },
        angle: 60,
        colors,
        scalar: 0.9,
        ticks: 200,
      });
      confetti({
        particleCount: 80,
        spread: 60,
        startVelocity: 45,
        origin: { x: 0.85, y: 0.85 },
        angle: 120,
        colors,
        scalar: 0.9,
        ticks: 200,
      });
    } else {
      confetti({
        particleCount: 24,
        spread: 40,
        startVelocity: 25,
        origin: { x: 0.5, y: 0.7 },
        colors,
        scalar: 0.7,
        ticks: 120,
      });
    }
    return true;
  } catch {
    return false;
  }
}

/** Dev helper — clear a celebration flag so you can re-experience it. */
export function resetCelebration(key: string): void {
  try {
    window.localStorage.removeItem(`kilat.celebrated.${key}`);
  } catch {
    /* swallow */
  }
}
